import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const ProfessionalPatients = () => {
  const { toast } = useToast();

  const { data: patients, isLoading } = useQuery({
    queryKey: ["professional-patients"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("professional_patients")
        .select(`
          *,
          patient:profiles!patient_id(*)
        `)
        .eq("professional_id", user.id);

      if (error) throw error;
      return data;
    },
  });

  const updatePatientStatus = async (patientId: string, status: string) => {
    const { error } = await supabase
      .from("professional_patients")
      .update({ status })
      .eq("patient_id", patientId);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de mettre à jour le statut du patient",
      });
    } else {
      toast({
        title: "Succès",
        description: "Le statut du patient a été mis à jour",
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Mes patients</h1>
        <p className="text-muted-foreground mt-2">
          Gérez vos relations avec vos patients
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {patients?.map((relation) => (
          <Card key={relation.id}>
            <CardHeader>
              <CardTitle>
                {relation.patient?.full_name || "Patient inconnu"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-medium">Statut:</p>
                <p className="text-muted-foreground capitalize">
                  {relation.status}
                </p>
              </div>
              {relation.status === "pending" && (
                <div className="flex gap-2">
                  <Button
                    onClick={() => updatePatientStatus(relation.patient_id, "active")}
                  >
                    Accepter
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updatePatientStatus(relation.patient_id, "rejected")}
                  >
                    Refuser
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ProfessionalPatients;